import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { AXIS_INFO } from './axisInfo';
import { OVERVIEW, wikiArticle } from './wikiContent';

// The plain-first DER read, built as a CASE (DER_SYNTHESIS_SPEC.md §10): first we say WHAT we looked
// at and HOW (the frame, so the claims have a basis), then the one headline claim (always visible),
// then the case as a stack of ONE-LINE claims that expand in place — so the writer sees the whole
// argument at a glance and follows only what they want. The heavy framework ("the D·E·R behind it")
// and the full "how to read this" live in the right-side WikiDrawer, not inline.

const NAVY = '#14536e';
const GREEN = '#1f7a5a';
const MUTE = '#5b7a8c';
const SLATE = '#3f5a6b';
const BAND_LABEL = { keep: 'In good shape', polish: 'Polish', rework: 'Needs a rework', rebuild: 'Big rebuild', cut_or_combine: 'Cut / combine' };
const BAND_COLOR = { keep: GREEN, polish: '#3b6fb0', rework: '#b06a14', rebuild: '#a23a2e', cut_or_combine: '#a23a2e' };
const AXES = ['contract', 'pressure', 'development', 'movement', 'consequence',
  'economy', 'assembly', 'clarity', 'freshness', 'unpredictability', 'charge', 'grip'];

// Build a wiki article for "the D·E·R behind this note" from the note's framework chip: list the
// axes it names with their plain definitions, then point at the full framework.
function derArticle(card) {
  const chip = (card && card.der_read) || '';
  const found = AXES.filter(a => new RegExp('\\b' + a + '\\b', 'i').test(chip));
  const body = [];
  if (chip) body.push(`In the craft framework, this note reads as: ${chip}.`);
  found.forEach(a => { const i = AXIS_INFO[a]; if (i) body.push(`${i.label} (${i.lens}) — ${i.measures}`); });
  body.push('Each axis lives inside one of three lenses — Design (is it built), Execution (does it play on the page), Read (does it grip). Open “How the Layered Read reads a scene” for the whole framework.');
  return { label: 'The D·E·R behind this note', short: chip || 'How this note maps to the craft framework.', body, kicker: 'D·E·R' };
}

// the method frame — the trust-builder. Open by default; collapses to a single line.
function Frame() {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: open ? '.8rem' : '.55rem', marginBottom: '.9rem' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'baseline', gap: 8, width: '100%' }}>
        <span style={{ fontSize: '.82rem', fontWeight: 800, letterSpacing: '.02em', color: NAVY, textTransform: 'uppercase' }}>The Layered Read read</span>
        <span style={{ fontSize: '.8rem', color: MUTE }}>· one of several passes on your script</span>
        <span style={{ marginLeft: 'auto', color: MUTE, fontSize: '.8rem' }}>{open ? 'hide ▲' : 'how we read it ▾'}</span>
      </button>
      {open && (
        <p style={{ margin: '.5rem 0 0', fontSize: '.92rem', color: '#33454f', lineHeight: 1.5 }}>
          We read it at every level — each <b>scene</b>, each <b>sequence</b>, each <b>act</b>, and the
          whole <b>script</b> — on three questions: <b>is it built</b> (Design), <b>does it play on the
          page</b> (Execution), <b>does it draw you in</b> (Involvement). Then we looked for the <b>patterns that
          repeat</b>. Here’s what stood out — most important first.
        </p>
      )}
    </div>
  );
}

// a labelled line inside an expanded claim (label = the question, readable navy — never an eyebrow)
function Line({ label, color, children }) {
  return (
    <div style={{ margin: '.45rem 0' }}>
      <span style={{ fontWeight: 700, color: color || NAVY }}>{label} </span>
      <span style={{ color: '#1f2d35' }}>{children}</span>
    </div>
  );
}

// a deeper inline expander, nested inside an expanded claim (why-it-happens, a test to try)
function Sub({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ display: 'inline-block', marginRight: 16 }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 600, fontSize: '.86rem' }}>
        <span style={{ color: MUTE }}>{open ? '▾' : '▸'}</span> {label}
      </button>
      {open && <div style={{ marginTop: 6, fontSize: '.88rem', color: '#33454f', lineHeight: 1.5 }}>{children}</div>}
    </span>
  );
}

function LightLink({ lit, onLight, onClear, surface = 'wheel' }) {
  // A real toggle, styled as one: outline chip = off ("highlight"), filled navy = on ("hide") — so its
  // interactivity and its current state are both obvious (a bare underlined link read as "navigate").
  return (
    <button type="button" onClick={() => lit ? onClear && onClear() : onLight && onLight()}
      className="sos-crumb"
      style={{ all: 'unset', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 700, fontSize: '.8rem',
        borderRadius: 8, padding: '.25rem .65rem', boxSizing: 'border-box',
        color: lit ? '#fff' : NAVY, background: lit ? NAVY : '#fff', border: `1.5px solid ${NAVY}` }}>
      {lit ? `◉ On the ${surface} · hide` : `◉ Highlight on the ${surface}`}
    </button>
  );
}

// one claim in the case: collapsed = a one-line claim (tag + title + sub-label); expands in place.
function CaseCard({ tag, tagColor, tagBg, accent, title, subLabel, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid #e7edf1', borderLeft: `3px solid ${accent}`, borderRadius: 8, margin: '.5rem 0', background: '#fff' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '.6rem .7rem', boxSizing: 'border-box' }}>
        <span style={{ color: MUTE, flexShrink: 0 }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: '.68rem', fontWeight: 800, color: tagColor, background: tagBg, borderRadius: 20, padding: '2px 9px', whiteSpace: 'nowrap', flexShrink: 0 }}>{tag}</span>
        <span style={{ fontWeight: 700, color: '#16242c', fontSize: '1rem', lineHeight: 1.25 }}>{title}</span>
        {subLabel && !open && <span style={{ marginLeft: 'auto', fontSize: '.8rem', color: MUTE, whiteSpace: 'nowrap', flexShrink: 0 }}>{subLabel}</span>}
      </button>
      {open && <div style={{ padding: '0 .85rem .8rem 2.0rem' }}>{children}</div>}
    </div>
  );
}

// ─── layout="findings" (icicle) — the "rewrite pass" taper ─────────────────────────────────────
// Sections are moments in a revision protocol, not finding kinds: Your first pass (the ONLY card,
// open, footprint auto-lit) → Then (one-liners) → After this pass, re-read for (symptoms: NOT
// tasks — recheck items) → footer: Protect / Don't get distracted by / Watch. Semantic inequality
// becomes geometric inequality (big → medium → quiet → small); sections render only if populated.
// Section headers are navy, full reading size — labels are questions, never eyebrows.

const SECTION_H = { fontSize: '.95rem', fontWeight: 700, color: NAVY, margin: '1.25rem 0 .45rem' };

// one-line row that expands in place to its detail (Then / re-read rows)
function ExpandRow({ prefix, title, lit, surface, onLight, onClear, children, quiet = false }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ margin: '.3rem 0' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ all: 'unset', cursor: 'pointer', display: 'block', fontSize: quiet ? '.92rem' : '.97rem', lineHeight: 1.5 }}>
        <span style={{ color: MUTE }}>{open ? '▾' : '▸'}</span>{' '}
        {prefix && <span style={{ fontWeight: 700, color: NAVY }}>{prefix} · </span>}
        <span style={{ color: quiet ? SLATE : '#16242c', fontWeight: quiet ? 500 : 600 }}>{title}</span>
      </button>
      {open && (
        <div style={{ margin: '.35rem 0 .5rem 1.15rem', fontSize: '.92rem', color: '#33454f', lineHeight: 1.5 }}>
          {children}
          {onLight && (
            <div style={{ marginTop: '.35rem' }}>
              <LightLink lit={lit} surface={surface} onLight={onLight} onClear={onClear} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// the method frame, demoted to a footer disclosure (trust copy is for AFTER reading, not before)
function FooterFrame() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: '1.4rem', paddingTop: '.8rem', borderTop: '1px solid #eef2f5' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ all: 'unset', cursor: 'pointer', color: MUTE, fontSize: '.86rem' }}>
        <span style={{ color: NAVY }}>ⓘ</span> how we read it — one of several passes on your script {open ? '▴' : '▾'}
      </button>
      {open && (
        <p style={{ margin: '.5rem 0 0', fontSize: '.9rem', color: '#33454f', lineHeight: 1.5 }}>
          We read it at every level — each <b>scene</b>, each <b>sequence</b>, each <b>act</b>, and the
          whole <b>script</b> — on three questions: <b>is it built</b> (Design), <b>does it play on the
          page</b> (Execution), <b>does it draw you in</b> (Involvement). Then we looked for the patterns that repeat.
        </p>
      )}
    </div>
  );
}

function FindingsLayout({ g, cards, rollup, t3, onHighlight, onClear, activeCardIdx, surfaceLabel, onOpenWiki }) {
  const derBtn = (card) => (card?.der_read || g.framework_read) && onOpenWiki && (
    <button type="button" onClick={() => onOpenWiki(derArticle(card && card.der_read ? card : { der_read: g.framework_read }))}
      style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 600, fontSize: '.86rem' }}>
      <span style={{ color: MUTE }}>ⓘ</span> The D·E·R behind it
    </button>
  );
  const primary = cards.find(c => c.role === 'primary_rewrite_pattern') || cards[0] || null;
  const thens = cards.filter(c => c !== primary && c.role === 'secondary_pattern');
  const symptoms = cards.filter(c => c !== primary && c.role === 'secondary_symptom');
  const watches = cards.filter(c => c !== primary && c.role === 'watch_item');
  const protects = cards.filter(c => c !== primary && c.role === 'protect_signal');
  const notFirst = g.not_first && g.not_first.title ? g.not_first : null;
  const idxOf = (c) => cards.indexOf(c);
  const refsOf = (c) => (c && c.footprint && c.footprint.refs) || [];
  const primaryRefs = refsOf(primary).length ? refsOf(primary) : ((g.footprint && g.footprint.refs) || []);
  const cov = (g.footprint && g.footprint.summary && /(\d+ of \d+)/.exec(g.footprint.summary)) || null;
  const covPhrase = cov ? `${cov[1]} scenes` : null;   // the card's TRUE coverage — refs only name exemplars
  const isStrength = g.verdict_band === 'keep';

  // NOTE: nothing is highlighted by default — the writer reads the finding first, then chooses to
  // light its footprint via the explicit CTA below (which then toggles to "hide"). Auto-lighting on
  // mount confused first-timers (a highlight they didn't ask for, with no obvious way to clear it).

  // The ROUTE: a framing sentence announces the structure ("what kind of list is this?"), then
  // numbered steps on a connecting spine — sequence is DRAWN, not implied by vocabulary, so a
  // connective like "then" always has a visible antecedent. Steps number dynamically.
  const steps = [];
  if (primary) steps.push('primary');
  if (thens.length) steps.push('thens');
  if (symptoms.length) steps.push('symptoms');
  const stepNo = (k) => steps.indexOf(k) + 1;
  const Chip = ({ n }) => (
    <span style={{ position: 'absolute', left: 0, top: -1, width: 24, height: 24, borderRadius: '50%', background: '#fff',
      border: `2px solid ${NAVY}`, color: NAVY, fontWeight: 700, fontSize: 12.5, display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>{n}</span>
  );
  const stepHead = { fontWeight: 700, color: NAVY, fontSize: '.97rem', margin: '1px 0 .45rem' };

  return (
    // cap the reading measure (~820px) so prose stays legible even when the card fills a wide pane.
    <div style={{ maxWidth: 820, padding: '1.25rem 1.4rem 1.3rem', fontSize: '.95rem', color: '#1f2d35', lineHeight: 1.5 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
        <h3 style={{ color: NAVY, fontWeight: 800, fontSize: '1.25rem', margin: 0 }}>{g.thesis_title}</h3>
        {g.verdict_band && (() => {
          const art = onOpenWiki ? wikiArticle('band', g.verdict_band) : null;
          const chip = (
            <span style={{ fontSize: '.78rem', fontWeight: 700, color: BAND_COLOR[g.verdict_band] || NAVY, cursor: art ? 'pointer' : 'default' }}>
              {BAND_LABEL[g.verdict_band] || g.verdict_band}{art ? ' ⓘ' : ''}
            </span>
          );
          return art
            ? <button type="button" onClick={() => onOpenWiki(art)} style={{ all: 'unset' }}>{chip}</button>
            : chip;
        })()}
      </div>
      {g.plain_thesis && <p style={{ margin: '.45rem 0 0', fontSize: '1rem' }}>{g.plain_thesis}</p>}

      {steps.length > 0 && (
        <div style={{ margin: '1.15rem 0 .75rem', fontWeight: 600, color: SLATE, fontSize: '.95rem' }}>
          {isStrength ? 'What to build on:' : 'The rewrite route — one pass at a time:'}
        </div>
      )}

      <div style={{ position: 'relative' }}>
        {steps.length > 1 && <div style={{ position: 'absolute', left: 11, top: 10, bottom: 10, width: 2, background: '#d7e2ea' }} />}

      {primary && (
        <div style={{ position: 'relative', paddingLeft: 38, marginBottom: '1.1rem' }}>
          <Chip n={stepNo('primary')} />
          <div style={stepHead}>{isStrength ? 'What’s carrying it' : 'First pass — start here'}</div>
          <div style={{ border: '1px solid #dbe5ec', borderLeft: `3px solid ${NAVY}`, borderRadius: 10, background: '#fff', padding: '.85rem 1rem' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, color: '#16242c', fontSize: '1.02rem', lineHeight: 1.3, flex: 1, minWidth: 240 }}>{primary.title || g.thesis_title}</span>
              {cov && (
                <span style={{ color: MUTE, fontSize: '.85rem', whiteSpace: 'nowrap' }}>
                  ◉ {cov[1]} scenes{' '}
                  <LightLink lit={activeCardIdx === idxOf(primary)} surface={surfaceLabel}
                    onLight={() => onHighlight && onHighlight(primaryRefs, idxOf(primary), primary.title || g.thesis_title, covPhrase)} onClear={onClear} />
                </span>
              )}
            </div>
            {primary.human_read && <p style={{ margin: '.5rem 0 0' }}>{primary.human_read}</p>}
            {g.why_this_first && <Line label="Why start here:">{g.why_this_first}</Line>}
            {g.first_move && <Line label="First move:">{g.first_move}</Line>}
            {(rollup || (t3 && t3.rewrite_experiment && t3.rewrite_experiment.experiment)) && (
              <div style={{ marginTop: '.55rem', paddingTop: '.45rem', borderTop: '1px solid #eef2f4' }}>
                {rollup && (
                  <Sub label="Why it happens">
                    {['scene_level', 'sequence_level', 'act_level', 'script_level'].map(k => {
                      const lv = rollup[k]; const text = typeof lv === 'string' ? lv : (lv && lv.text);
                      return text ? <div key={k} style={{ marginBottom: 5 }}><b style={{ color: SLATE, textTransform: 'capitalize' }}>{k.replace('_level', '')}: </b>{text}</div> : null;
                    })}
                  </Sub>
                )}
                {t3 && t3.rewrite_experiment && t3.rewrite_experiment.experiment && (
                  <Sub label="A test to try">
                    <div>{t3.rewrite_experiment.experiment}</div>
                    {(t3.success_tests || []).map((s, i) => <div key={i} style={{ color: GREEN, marginTop: 4 }}><b>You’ll know it worked if:</b> {s.test}</div>)}
                  </Sub>
                )}
                {derBtn(primary)}
              </div>
            )}
          </div>
        </div>
      )}

      {thens.length > 0 && (
        <div style={{ position: 'relative', paddingLeft: 38, marginBottom: '1.1rem' }}>
          <Chip n={stepNo('thens')} />
          <div style={stepHead}>Next</div>
          {thens.map((c, i) => (
            <ExpandRow key={i} title={c.title} surface={surfaceLabel}
              lit={activeCardIdx === idxOf(c)}
              onLight={refsOf(c).length ? () => onHighlight && onHighlight(refsOf(c), idxOf(c), c.title) : null}
              onClear={onClear}>
              {c.human_read && <p style={{ margin: 0 }}>{c.human_read}</p>}
              {c.writer_action && <Line label="Try:">{c.writer_action}</Line>}
              {c.der_read && onOpenWiki && <div style={{ marginTop: '.3rem' }}>{derBtn(c)}</div>}
            </ExpandRow>
          ))}
        </div>
      )}

      {symptoms.length > 0 && (
        <div style={{ position: 'relative', paddingLeft: 38 }}>
          <Chip n={stepNo('symptoms')} />
          <div style={stepHead}>Then re-read — before fixing anything else</div>
          {symptoms.map((c, i) => (
            <ExpandRow key={i} title={c.title} quiet surface={surfaceLabel}
              lit={activeCardIdx === idxOf(c)}
              onLight={refsOf(c).length ? () => onHighlight && onHighlight(refsOf(c), idxOf(c), c.title) : null}
              onClear={onClear}>
              {c.human_read && <p style={{ margin: 0 }}>{c.human_read}</p>}
            </ExpandRow>
          ))}
          <p style={{ margin: '.4rem 0 0 1.15rem', fontSize: '.88rem', color: MUTE, lineHeight: 1.5 }}>
            Expect some of this to ease as the fixes above land — look again before treating it as a
            separate problem.
          </p>
        </div>
      )}
      </div>

      {(protects.length > 0 || g.protect_short || notFirst || watches.length > 0) && (
        <div style={{ marginTop: '1.3rem', paddingTop: '.9rem', borderTop: '1px dotted #d3dde3', fontSize: '.9rem', color: SLATE, lineHeight: 1.55 }}>
          <div style={{ fontWeight: 700, color: SLATE, marginBottom: '.3rem' }}>While you work</div>
          {(protects.length > 0 || g.protect_short) && (
            <div style={{ margin: '.15rem 0' }}>
              <span style={{ fontWeight: 700, color: GREEN }}>Protect</span>
              {' '}— {protects[0]?.title || 'keep this while you rewrite'}{g.protect_short ? `. ${g.protect_short}` : (protects[0]?.human_read ? `. ${protects[0].human_read}` : '')}
            </div>
          )}
          {notFirst && (
            <div style={{ margin: '.15rem 0' }}>
              <span style={{ fontWeight: 700, color: NAVY }}>Don’t get distracted by</span>
              {' '}— {notFirst.title}{notFirst.note ? `. ${notFirst.note}` : ''}
            </div>
          )}
          {watches.map((c, i) => (
            <div key={i} style={{ margin: '.15rem 0' }}>
              <span style={{ fontWeight: 700, color: SLATE }}>Watch</span> — {c.title}
            </div>
          ))}
        </div>
      )}

      <FooterFrame />
    </div>
  );
}

// surfaceLabel: what the footprint lights up on — 'wheel' (SoS page, default) or 'timeline' (icicle).
// layout: 'case' (default, the SoS page's stacked claims) or 'findings' (icicle: the rewrite-pass taper).
export default function SynthesisPanel({ synthesis, onHighlight, onClear, activeCardIdx, onOpenWiki, compact = false, surfaceLabel = 'wheel', layout = 'case' }) {
  const wv = synthesis && synthesis.writer_view;
  const g = wv && wv.tier_1_glance;
  if (!g) return null;
  const t2 = wv.tier_2_diagnosis || {};
  const t3 = wv.tier_3_rewrite || {};
  const cards = g.pattern_cards || [];
  const rollup = t2.rollup_explanation || null;
  const isStrength = g.verdict_band === 'keep';

  const primary = cards.find(c => c.role === 'primary_rewrite_pattern');
  const protectCard = cards.find(c => c.role === 'protect_signal');
  const nextCards = cards.filter(c => c.role !== 'protect_signal' && c.role !== 'primary_rewrite_pattern');
  const primaryRefs = (g.footprint && g.footprint.refs) || (primary && primary.footprint && primary.footprint.refs) || [];
  const notFirst = g.not_first && g.not_first.title ? g.not_first : null;
  const openDer = (card) => onOpenWiki && onOpenWiki(derArticle(card));

  // a terse leverage phrase for the Start-here collapsed line (pull the "N of M" coverage if present)
  const cov = (g.footprint && g.footprint.summary && /(\d+ of \d+)/.exec(g.footprint.summary)) || null;
  const startSub = cov ? `touches ${cov[1]} scenes` : 'the most connected note';

  if (layout === 'findings') {
    return <FindingsLayout g={g} cards={cards} rollup={rollup} t3={t3}
      onHighlight={onHighlight} onClear={onClear} activeCardIdx={activeCardIdx} surfaceLabel={surfaceLabel}
      onOpenWiki={onOpenWiki} />;
  }

  return (
    <div style={{ padding: '0 1.15rem 1.3rem', fontSize: '.95rem', color: '#1f2d35', lineHeight: 1.5 }}>
      {/* In compact mode the frame + headline + plain thesis live in the full-width ReadHeader (Zone A),
          so we skip them here and lead straight with the case cards (the patterns). */}
      {!compact && <Frame />}

      {!compact && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
          <h3 style={{ color: NAVY, fontWeight: 800, fontSize: '1.2rem', margin: 0 }}>{g.thesis_title}</h3>
          {g.verdict_band && (
            <span style={{ fontSize: '.74rem', fontWeight: 700, color: BAND_COLOR[g.verdict_band] || NAVY }}>
              {BAND_LABEL[g.verdict_band] || g.verdict_band}
            </span>
          )}
        </div>
      )}
      {!compact && g.plain_thesis && <p style={{ margin: '.4rem 0 1rem', fontSize: '1.0rem' }}>{g.plain_thesis}</p>}

      <div style={{ fontSize: '.9rem', fontWeight: 700, color: SLATE, margin: compact ? '.2rem 0 .2rem' : '0 0 .2rem' }}>
        Here’s the case — most important first
      </div>

      {/* 3 — the case: collapsed claims that expand in place */}
      <CaseCard tag={isStrength ? '✓ Overall' : 'Start here'} tagColor="#fff" tagBg={isStrength ? GREEN : NAVY}
        accent={NAVY} title={(primary && primary.title) || g.thesis_title} subLabel={startSub}>
        {primary && primary.human_read && <p style={{ margin: '0 0 .4rem' }}>{primary.human_read}</p>}
        {g.why_this_first && <Line label="Why start here:">{g.why_this_first}</Line>}
        {g.footprint && g.footprint.summary && (
          <Line label="Where:">
            {g.footprint.summary}{'  '}
            <LightLink lit={activeCardIdx === 0} surface={surfaceLabel}
              onLight={() => onHighlight && onHighlight(primaryRefs, 0, (primary && primary.title) || g.thesis_title)} onClear={onClear} />
          </Line>
        )}
        {g.first_move && <Line label="Do this first:">{g.first_move}</Line>}
        <div style={{ marginTop: '.6rem', paddingTop: '.5rem', borderTop: '1px solid #eef2f4' }}>
          {rollup && (
            <Sub label="Why it happens">
              {['scene_level', 'sequence_level', 'act_level', 'script_level'].map(k => {
                const lv = rollup[k]; const text = typeof lv === 'string' ? lv : (lv && lv.text);
                return text ? <div key={k} style={{ marginBottom: 5 }}><b style={{ color: SLATE, textTransform: 'capitalize' }}>{k.replace('_level', '')}: </b>{text}</div> : null;
              })}
            </Sub>
          )}
          {t3.rewrite_experiment && t3.rewrite_experiment.experiment && (
            <Sub label="A test to try">
              <div>{t3.rewrite_experiment.experiment}</div>
              {(t3.success_tests || []).map((s, i) => <div key={i} style={{ color: GREEN, marginTop: 4 }}><b>You’ll know it worked if:</b> {s.test}</div>)}
            </Sub>
          )}
          {g.framework_read && onOpenWiki && (
            <button type="button" onClick={() => openDer(primary || { der_read: g.framework_read })}
              style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 600, fontSize: '.86rem' }}>
              <span style={{ color: MUTE }}>ⓘ</span> The D·E·R behind it
            </button>
          )}
        </div>
      </CaseCard>

      {nextCards.map((c, i) => {
        const idx = i + 1;
        const refs = (c.footprint && c.footprint.refs) || [];
        return (
          <CaseCard key={i} tag={`Next · ${idx + 1}`} tagColor={SLATE} tagBg="#eef3f6" accent="#d3dde3"
            title={c.title} subLabel="after the main note">
            {c.human_read && <p style={{ margin: '0 0 .4rem' }}>{c.human_read}</p>}
            {c.why_not_first && <Line label="Why not first:" color={MUTE}>{c.why_not_first}</Line>}
            {c.writer_action && <Line label="Try:">{c.writer_action}</Line>}
            {refs.length > 0 && (
              <div style={{ margin: '.35rem 0' }}>
                <LightLink lit={activeCardIdx === idx} surface={surfaceLabel}
                  onLight={() => onHighlight && onHighlight(refs, idx, c.title)} onClear={onClear} />
              </div>
            )}
            {c.der_read && onOpenWiki && (
              <button type="button" onClick={() => openDer(c)}
                style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 600, fontSize: '.86rem', marginTop: 4 }}>
                <span style={{ color: MUTE }}>ⓘ</span> The D·E·R behind it
              </button>
            )}
          </CaseCard>
        );
      })}

      {(g.protect_short || protectCard) && (
        <CaseCard tag="Protect" tagColor="#fff" tagBg={GREEN} accent={GREEN}
          title={(protectCard && protectCard.title) || 'Keep this while you rewrite'} subLabel="don’t break it">
          <p style={{ margin: 0 }}>{g.protect_short || (protectCard && protectCard.human_read)}</p>
        </CaseCard>
      )}

      {notFirst && (
        <CaseCard tag="Not first" tagColor={MUTE} tagBg="#f1f4f6" accent="#cdd6db"
          title={notFirst.title} subLabel="won’t fix the main drag yet">
          {notFirst.note && <p style={{ margin: 0, color: SLATE }}>{notFirst.note}</p>}
        </CaseCard>
      )}
    </div>
  );
}

SynthesisPanel.propTypes = {
  synthesis: PropTypes.object,
  onHighlight: PropTypes.func,
  onClear: PropTypes.func,
  activeCardIdx: PropTypes.number,
  onOpenWiki: PropTypes.func,
  compact: PropTypes.bool,
  surfaceLabel: PropTypes.string,
  layout: PropTypes.oneOf(['case', 'findings']),
};
